import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import { 
  format, addMonths, subMonths, startOfMonth, endOfMonth, 
  eachDayOfInterval, isSameDay, isWithinInterval, 
  subDays, subWeeks, subMonths as dateFnsSubMonths, startOfYear
} from 'date-fns';
import './DatePickerModal.css';

const DatePickerModal = ({ onClose, onApply, initialRange }) => {
  const [currentMonth, setCurrentMonth] = useState(initialRange.start);
  const nextMonth = addMonths(currentMonth, 1);
  
  const [tempRange, setTempRange] = useState(initialRange);
  const [selecting, setSelecting] = useState('start'); // 'start' or 'end'

  const presets = [
    { label: 'Today', getValue: () => ({ start: new Date(), end: new Date() }) },
    { label: 'Last 7 days', getValue: () => ({ start: subDays(new Date(), 7), end: new Date() }) },
    { label: 'Last 4 weeks', getValue: () => ({ start: subWeeks(new Date(), 4), end: new Date() }) },
    { label: 'Last 3 months', getValue: () => ({ start: dateFnsSubMonths(new Date(), 3), end: new Date() }) },
    { label: 'Week to date', getValue: () => ({ start: subDays(new Date(), new Date().getDay()), end: new Date() }) },
    { label: 'Month to date', getValue: () => ({ start: startOfMonth(new Date()), end: new Date() }) },
    { label: 'Year to date', getValue: () => ({ start: startOfYear(new Date()), end: new Date() }) },
    { label: 'All time', getValue: () => ({ start: subDays(new Date(), 365), end: new Date() }) }
  ];

  const handleDayClick = (day) => {
    if (selecting === 'start') {
      setTempRange({ start: day, end: day });
      setSelecting('end');
    } else {
      if (day < tempRange.start) {
        setTempRange({ start: day, end: tempRange.start });
      } else {
        setTempRange({ ...tempRange, end: day });
      }
      setSelecting('start');
    }
  };

  const handlePresetClick = (preset) => {
    setTempRange(preset.getValue());
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="date-range-selector">
            <Clock size={16} />
            <span>Date Range: <strong>Between</strong></span>
            <ChevronRight size={14} className="rotate-90" />
          </div>
        </div>

        <div className="modal-body">
          <div className="picker-sidebar">
            {presets.map(preset => (
              <div 
                key={preset.label} 
                className={`preset-item ${isSameDay(tempRange.start, preset.getValue().start) ? 'active' : ''}`}
                onClick={() => handlePresetClick(preset)}
              >
                {preset.label}
              </div>
            ))}
          </div>

          <div className="calendars-container">
            <div className="calendar-pane">
              <div className="calendar-header">
                <ChevronLeft className="nav-icon" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))} />
                <span>{format(currentMonth, 'MMMM yyyy')}</span>
                <div style={{ width: 20 }}></div>
              </div>
              <Calendar month={currentMonth} range={tempRange} onDayClick={handleDayClick} />
              <div className="time-picker">
                <Clock size={14} />
                <span>12:00:00 AM</span>
                <span className="timezone">UTC-04</span>
              </div>
            </div>

            <div className="calendar-pane">
              <div className="calendar-header">
                <div style={{ width: 20 }}></div>
                <span>{format(nextMonth, 'MMMM yyyy')}</span>
                <ChevronRight className="nav-icon" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))} />
              </div>
              <Calendar month={nextMonth} range={tempRange} onDayClick={handleDayClick} />
              <div className="time-picker">
                <Clock size={14} />
                <span>11:59:59 PM</span>
                <span className="timezone">UTC-04</span>
              </div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <span className="range-preview">
            {format(tempRange.start, 'MMM dd, yyyy')} - {format(tempRange.end, 'MMM dd, yyyy')}
          </span>
          <div className="footer-btns">
            <button className="cancel-btn" onClick={onClose}>Cancel</button>
            <button className="apply-btn" onClick={() => onApply(tempRange)}>Apply</button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Calendar = ({ month, range, onDayClick }) => {
  const start = startOfMonth(month);
  const end = endOfMonth(month);
  const days = eachDayOfInterval({ start, end });
  const padding = Array(start.getDay()).fill(null);
  const weekDays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  return (
    <div className="calendar-grid">
      {weekDays.map(d => <div key={d} className="weekday-label">{d}</div>)}
      {padding.map((_, i) => <div key={`pad-${i}`} className="empty-day"></div>)}
      {days.map(day => {
        const isSelected = isSameDay(day, range.start) || isSameDay(day, range.end);
        const isInRange = isWithinInterval(day, { start: range.start, end: range.end });
        
        return (
          <div 
            key={day.toString()} 
            className={`calendar-day ${isSelected ? 'selected' : ''} ${isInRange ? 'in-range' : ''}`}
            onClick={() => onDayClick(day)}
          >
            {format(day, 'd')}
          </div>
        );
      })}
    </div>
  );
};

export default DatePickerModal;
